import { ISaveDeliveryAddressInput, IsoCountryCode } from 'generated/graphql-gateway';
import { UserDetails } from 'state/auth/hooks/types';
import { sanitizePhoneNumber } from 'utils/form';
import { IPlaceAddress } from 'utils/geolocation';

import { addressExists } from './utils';

export type SaveDeliveryAddressFn = (
  input: ISaveDeliveryAddressInput,
  options?: { shouldThrowException?: boolean }
) => Promise<void>;

export interface SaveDeliveryAddressIfRequestedParams {
  shouldSave: boolean;
  user?: UserDetails | null;
  address: IPlaceAddress;
  phoneNumber: string;
  saveDeliveryAddress: SaveDeliveryAddressFn;
}

function toSaveDeliveryAddressInput(
  address: IPlaceAddress,
  phoneNumber: string,
  alias: string
): ISaveDeliveryAddressInput {
  return {
    addressLine1: address.addressLine1,
    addressLine2: address.addressLine2,
    alias,
    city: address.city,
    state: address.state,
    zip: address.zip,
    country: address.country as IsoCountryCode,
    phoneNumber,
    defaultInstruction: address.defaultInstruction,
    route: address.route,
    streetNumber: address.streetNumber,
    latitude: address.latitude,
    longitude: address.longitude,
  };
}

async function resolveSavePhoneNumber(
  phoneNumber: string,
  user: UserDetails,
  address: IPlaceAddress
): Promise<string | null> {
  const rawPhone = phoneNumber.trim() || user.details?.phoneNumber || '';
  if (!rawPhone) {
    return null;
  }

  const country = address.country || user.details?.isoCountryCode || 'USA';

  try {
    return (await sanitizePhoneNumber(rawPhone, country)) || null;
  } catch {
    return null;
  }
}

export async function saveDeliveryAddressIfRequested({
  shouldSave,
  user,
  address,
  phoneNumber,
  saveDeliveryAddress,
}: SaveDeliveryAddressIfRequestedParams): Promise<boolean> {
  if (!shouldSave || !user?.cognitoId || !user.details) {
    return false;
  }

  if (!address.addressLine1 || !address.city) {
    return false;
  }

  const resolvedPhone = await resolveSavePhoneNumber(phoneNumber, user, address);
  if (!resolvedPhone) {
    return false;
  }

  const alias = address.alias || '';
  const enrichedAddress: IPlaceAddress = {
    ...address,
    alias,
    phoneNumber: resolvedPhone,
  };

  if (addressExists(user.details.deliveryAddresses || [], enrichedAddress)) {
    return true;
  }

  await saveDeliveryAddress(toSaveDeliveryAddressInput(address, resolvedPhone, alias));
  return true;
}
